import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Shield, UserPlus, Check, AlertCircle, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { Avatar } from '../ui';

/**
 * Add User Permission Modal
 * Allows adding users from the same sponsor with specific permission levels
 */
const AddUserPermissionModal = ({ isOpen, onClose, assessmentId, onUserAdded }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [usersByRole, setUsersByRole] = useState({});
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [permissionLevel, setPermissionLevel] = useState('edit');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sponsorName, setSponsorName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Permission level options
  const permissionLevels = [
    {
      value: 'full_admin',
      label: 'Full Admin',
      description: 'Complete control - can edit, publish, delete, and manage users',
      color: 'purple',
      icon: Shield
    },
    {
      value: 'edit',
      label: 'Edit Access',
      description: 'Can edit questions, screens, and content but cannot publish or delete',
      color: 'blue',
      icon: UserPlus
    },
    {
      value: 'view',
      label: 'View Only',
      description: 'Can view assessment but cannot make changes',
      color: 'gray',
      icon: Check
    }
  ];

  // Fetch available users when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAvailableUsers();
      setSelectedUsers(new Set());
      setPermissionLevel('edit');
      setError('');
      setSuccess('');
    }
  }, [isOpen, assessmentId]);

  // Filter users based on search and role filter
  useEffect(() => {
    let filtered = availableUsers;
    
    // Apply role filter first
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }
    
    // Then apply search filter
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.email.toLowerCase().includes(term) ||
          `${user.first_name} ${user.last_name}`.toLowerCase().includes(term) ||
          user.role.toLowerCase().includes(term)
      );
    }
    
    setFilteredUsers(filtered);
  }, [searchTerm, availableUsers, roleFilter]);

  const fetchAvailableUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${assessmentId}/available_users/`);
      const data = response.data;
      setAvailableUsers(data.users || []);
      setFilteredUsers(data.users || []);
      setUsersByRole(data.users_by_role || {});
      setSponsorName(data.sponsor_name || '');
      setRoleFilter('all');
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSubmit = async () => {
    if (selectedUsers.size === 0) {
      setError('Please select at least one user');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(
        `/api/assessment-builder/assessments/${assessmentId}/add_users/`,
        {
          user_ids: Array.from(selectedUsers),
          permission_level: permissionLevel
        }
      );
      
      setSuccess(`Successfully added ${selectedUsers.size} user(s) with ${permissionLevel.replace('_', ' ')} access`);
      
      // Clear selections
      setSelectedUsers(new Set());
      setSearchTerm('');
      
      // Callback to refresh usage data
      if (onUserAdded) {
        onUserAdded();
      }
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error adding users:', err);
      setError(err.message || 'Failed to add users. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedLevel = permissionLevels.find(p => p.value === permissionLevel);
  const LevelIcon = selectedLevel?.icon || Shield;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">
                  Add Users to Assessment
                </h2>
                <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
                  Grant access to users from your sponsor organization
                </p>
              </div>
              <button
                onClick={onClose}
                className="btn btn-ghost btn-sm btn-icon -mr-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Content: the permission picker and search stay pinned; only the
              user list scrolls, so there is a single scrollbar instead of a
              nested outer+inner pair (which felt wonky). */}
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-hidden px-6 py-5">
            {/* Permission Level Selection */}
            <div className="flex-shrink-0">
              <label className="label">
                Select Permission Level
              </label>
              <div className="grid grid-cols-3 gap-3">
                {permissionLevels.map((level) => {
                  const Icon = level.icon;
                  const isSelected = permissionLevel === level.value;
                  return (
                    <button
                      key={level.value}
                      onClick={() => setPermissionLevel(level.value)}
                      className={`relative rounded-xl border p-4 text-left transition-colors ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50/40 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-400/10 dark:ring-primary-400'
                          : 'border-gray-200/80 bg-white shadow-card hover:border-gray-300 dark:border-white/[0.07] dark:bg-gray-800 dark:hover:border-white/15'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute right-3 top-3 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-primary-600 dark:bg-primary-500">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                      <Icon className={`mb-2.5 h-[18px] w-[18px] ${isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
                      <h4 className="mb-0.5 text-[13.5px] font-medium text-gray-900 dark:text-gray-50">
                        {level.label}
                      </h4>
                      <p className="line-clamp-2 text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">
                        {level.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Search and Filter Users */}
            <div className="flex-shrink-0">
              <label className="label">
                Search Users {sponsorName && <span className="font-normal text-gray-500 dark:text-gray-400">from {sponsorName}</span>}
              </label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name or email..."
                    className="input pl-9"
                  />
                </div>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="input w-auto min-w-[160px]"
                >
                  <option value="all">All Roles ({availableUsers.length})</option>
                  {Object.keys(usersByRole).sort().map(role => (
                    <option key={role} value={role}>
                      {role} ({usersByRole[role]?.length || 0})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* User List: flexes to fill the remaining modal height and owns the
                only scroll region in the body. */}
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="mb-2 flex flex-shrink-0 items-center justify-between">
                <label className="block text-[13px] font-medium text-gray-800 dark:text-gray-200">
                  Select Users ({selectedUsers.size} selected)
                </label>
                {selectedUsers.size > 0 && (
                  <button
                    onClick={() => setSelectedUsers(new Set())}
                    className="text-xs font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    Clear selection
                  </button>
                )}
              </div>

              {loading ? (
                <div className="py-12 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
                  <p className="mt-3 text-[13px] text-gray-500 dark:text-gray-400">Loading users...</p>
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-200 py-12 text-center dark:border-white/10">
                  <AlertCircle className="mx-auto mb-2 h-6 w-6 text-gray-400 dark:text-gray-500" />
                  <p className="text-[13.5px] text-gray-500 dark:text-gray-400">
                    {searchTerm ? 'No users found matching your search' : 'No available users'}
                  </p>
                </div>
              ) : (
                <div className="min-h-0 flex-1 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200/80 dark:divide-white/[0.06] dark:border-white/[0.07]">
                  {filteredUsers.map((user) => {
                    const isSelected = selectedUsers.has(user.id);
                    return (
                      <button
                        key={user.id}
                        onClick={() => toggleUserSelection(user.id)}
                        className={`flex w-full items-center justify-between gap-3 px-3.5 py-2.5 transition-colors ${
                          isSelected ? 'bg-primary-50/40 dark:bg-primary-400/10' : 'hover:bg-gray-50/80 dark:hover:bg-white/[0.03]'
                        }`}
                      >
                        <div className="flex flex-1 items-center gap-3 text-left">
                          <Avatar name={`${user.first_name || ''} ${user.last_name || ''}`} size="sm" tone={isSelected ? 'brand' : 'gray'} />
                          <div className="min-w-0 flex-1">
                            <h4 className="truncate text-[13.5px] font-medium text-gray-900 dark:text-gray-50">
                              {user.first_name} {user.last_name}
                            </h4>
                            <p className="truncate text-[12.5px] text-gray-500 dark:text-gray-400">{user.email}</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">{user.role_display || user.role}</p>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 dark:bg-primary-500">
                            <Check className="h-3.5 w-3.5 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Error/Success Messages */}
            {error && (
              <div className="flex flex-shrink-0 items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3.5 dark:border-red-500/30 dark:bg-red-500/10">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400" />
                <p className="text-[13px] text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            {success && (
              <div className="flex flex-shrink-0 items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3.5 dark:border-emerald-500/30 dark:bg-emerald-500/10">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                <p className="text-[13px] text-emerald-700 dark:text-emerald-300">{success}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-[13px] text-gray-500 dark:text-gray-400">
                <LevelIcon className="mr-1.5 inline h-4 w-4 align-text-bottom text-gray-400 dark:text-gray-500" />
                Granting <span className="font-medium text-gray-900 dark:text-gray-50">{selectedLevel?.label}</span> to {selectedUsers.size} user(s)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  disabled={submitting}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || selectedUsers.size === 0}
                  className="btn btn-primary"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4" />
                      Add Users
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddUserPermissionModal;
